import React, { useMemo, memo, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, BarChart, Bar, ComposedChart, Area } from 'recharts';
import { TrendingUp, X, Clock, Scissors, StopCircle, ArrowLeftRight } from 'lucide-react';

// Completely isolated chart component with heavy memoization
// This component MUST NOT receive state setters or non-primitive values that change on every render

const GoalsEvolutionChart = memo(function GoalsEvolutionChart({ 
  goal, 
  onClose,
  isDarkMode = false
}) {
  // Local state for chart view mode
  const [viewMode, setViewMode] = useState('efficiency'); // 'efficiency', 'times', 'comparison'
  
  // Memoize chart data transformation to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    const dailyData = goal?.real_metrics?.daily_data || goal?.daily_breakdown || [];
    const simulatedData = goal?.simulated_metrics?.daily_data || [];
    
    if (!Array.isArray(dailyData) || dailyData.length === 0) {
      return [];
    }
    
    return dailyData.map((day, index) => {
      const simDay = simulatedData[index] || {};
      return {
        date: day.date ? new Date(day.date).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) : '',
        // Efficiency data
        realEfficiency: typeof day.efficiency === 'number' ? Number(day.efficiency.toFixed(1)) : 0,
        simEfficiency: typeof simDay.efficiency === 'number' ? Number(simDay.efficiency.toFixed(1)) : 0,
        // Time data (in minutes)
        cuttingTime: day.cutting_time || 0,
        machineStopped: day.machine_stopped || 0,
        betweenMarkers: day.between_markers || 0,
        totalTime: day.total_time || 0,
        // Simulated time data
        simCuttingTime: simDay.cutting_time || 0,
        simMachineStopped: simDay.machine_stopped || 0,
        simBetweenMarkers: simDay.between_markers || 0,
        // Calculated percentages (relative to cutting time)
        stoppedPercent: day.cutting_time > 0 ? Number(((day.machine_stopped / day.cutting_time) * 100).toFixed(1)) : 0,
        betweenPercent: day.cutting_time > 0 ? Number(((day.between_markers / day.cutting_time) * 100).toFixed(1)) : 0
      };
    });
  }, [goal?.real_metrics?.daily_data, goal?.simulated_metrics?.daily_data, goal?.daily_breakdown]);

  // Memoize target values
  const targets = useMemo(() => ({
    efficiency: goal?.parameters?.target_efficiency || 35,
    betweenMarkers: goal?.parameters?.target_between_markers_time || 2,
    machineStopped: goal?.parameters?.target_machine_stopped_percent || 10
  }), [goal?.parameters]);

  // Memoize averages
  const averages = useMemo(() => {
    if (!goal?.real_metrics || !goal?.simulated_metrics) {
      return { real: 0, simulated: 0, gain: 0, avgStopped: 0, avgBetween: 0 };
    }
    const realData = goal.real_metrics.daily_data || [];
    const totalCutting = realData.reduce((sum, d) => sum + (d.cutting_time || 0), 0);
    const totalStopped = realData.reduce((sum, d) => sum + (d.machine_stopped || 0), 0);
    const totalBetween = realData.reduce((sum, d) => sum + (d.between_markers || 0), 0);
    
    return {
      real: goal.real_metrics.efficiency || 0,
      simulated: goal.simulated_metrics.efficiency || 0,
      gain: goal.simulated_metrics.efficiency_gain || 0,
      avgStopped: totalCutting > 0 ? (totalStopped / totalCutting) * 100 : 0,
      avgBetween: totalCutting > 0 ? (totalBetween / totalCutting) * 100 : 0
    };
  }, [goal?.real_metrics, goal?.simulated_metrics]);

  // Theme colors - memoized
  const colors = useMemo(() => ({
    bg: isDarkMode ? 'bg-slate-800/90' : 'bg-white',
    text: isDarkMode ? 'text-white' : 'text-slate-900',
    textMut: isDarkMode ? 'text-slate-400' : 'text-slate-500',
    border: isDarkMode ? 'border-slate-700' : 'border-slate-200',
    grid: isDarkMode ? '#334155' : '#e2e8f0',
    axis: isDarkMode ? '#94a3b8' : '#64748b'
  }), [isDarkMode]);

  // Handle view mode change
  const handleViewChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  if (!goal) {
    return null;
  }

  if (chartData.length === 0) {
    return (
      <div className={`${colors.bg} rounded-xl p-6 border ${colors.border}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-violet-500" />
            <h3 className={`font-semibold ${colors.text}`}>{goal.name}</h3>
          </div>
          <button 
            onClick={onClose}
            className={`p-1.5 rounded-lg hover:bg-slate-500/10 ${colors.textMut}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className={`text-center py-8 ${colors.textMut}`}>
          Sem dados diários disponíveis para este período.
        </p>
      </div>
    );
  }

  return (
    <div className={`${colors.bg} rounded-xl p-6 border ${colors.border}`} data-testid="goals-evolution-chart">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-violet-500/10">
            <TrendingUp className="w-5 h-5 text-violet-500" />
          </div>
          <div>
            <h3 className={`font-semibold ${colors.text}`}>Evolução: {goal.name}</h3>
            <p className={`text-xs ${colors.textMut}`}>
              {chartData.length} dias de dados
            </p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className={`p-1.5 rounded-lg hover:bg-slate-500/10 ${colors.textMut}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => handleViewChange('efficiency')}
          className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-colors ${
            viewMode === 'efficiency' 
              ? 'bg-violet-500 text-white' 
              : `${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} ${colors.textMut} hover:opacity-80`
          }`}
        >
          <TrendingUp className="w-3 h-3" />
          Eficiência
        </button>
        <button
          onClick={() => handleViewChange('times')}
          className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-colors ${
            viewMode === 'times' 
              ? 'bg-cyan-500 text-white' 
              : `${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} ${colors.textMut} hover:opacity-80`
          }`}
        >
          <Clock className="w-3 h-3" />
          Tempos
        </button>
        <button
          onClick={() => handleViewChange('percentages')}
          className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-colors ${
            viewMode === 'percentages' 
              ? 'bg-amber-500 text-white' 
              : `${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} ${colors.textMut} hover:opacity-80`
          }`}
        >
          <StopCircle className="w-3 h-3" />
          % Parada / E.Marc
        </button>
      </div>

      {/* Summary Stats based on view mode */}
      {viewMode === 'efficiency' && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1`}>Eficiência Real</p>
            <p className="text-xl font-bold text-cyan-500">{averages.real.toFixed(1)}%</p>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1`}>Eficiência Simulada</p>
            <p className="text-xl font-bold text-violet-500">{averages.simulated.toFixed(1)}%</p>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1`}>Ganho Potencial</p>
            <p className="text-xl font-bold text-emerald-500">+{averages.gain.toFixed(1)}pp</p>
          </div>
        </div>
      )}

      {viewMode === 'times' && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1 flex items-center gap-1`}>
              <Scissors className="w-3 h-3 text-cyan-500" /> Tempo Corte
            </p>
            <p className="text-xl font-bold text-cyan-500">
              {(goal.real_metrics?.total_cutting_time / 60 || 0).toFixed(1)}h
            </p>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1 flex items-center gap-1`}>
              <StopCircle className="w-3 h-3 text-red-500" /> Máq. Parada
            </p>
            <p className="text-xl font-bold text-red-500">
              {(goal.real_metrics?.total_machine_stopped / 60 || 0).toFixed(1)}h
            </p>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1 flex items-center gap-1`}>
              <ArrowLeftRight className="w-3 h-3 text-amber-500" /> Entre Marcadas
            </p>
            <p className="text-xl font-bold text-amber-500">
              {(goal.real_metrics?.total_between_markers / 60 || 0).toFixed(1)}h
            </p>
          </div>
        </div>
      )}

      {viewMode === 'percentages' && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1`}>% Parada (média vs T.Corte)</p>
            <p className={`text-xl font-bold ${averages.avgStopped <= (targets.machineStopped || 50) ? 'text-emerald-500' : 'text-red-500'}`}>
              {averages.avgStopped.toFixed(1)}%
            </p>
            <p className={`text-xs ${colors.textMut}`}>Meta: ≤{targets.machineStopped}%</p>
          </div>
          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
            <p className={`text-xs ${colors.textMut} mb-1`}>% E.Marc (média vs T.Corte)</p>
            <p className="text-xl font-bold text-amber-500">
              {averages.avgBetween.toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          {viewMode === 'efficiency' ? (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.axis }} tickLine={{ stroke: colors.axis }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: colors.axis }} tickLine={{ stroke: colors.axis }} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '8px'
                }}
                formatter={(value, name) => {
                  const labels = { realEfficiency: 'Eficiência Real', simEfficiency: 'Eficiência Simulada' };
                  return [`${value}%`, labels[name] || name];
                }}
              />
              <Legend formatter={(v) => v === 'realEfficiency' ? 'Eficiência Real' : 'Eficiência Simulada'} />
              <ReferenceLine y={targets.efficiency} stroke="#10b981" strokeDasharray="5 5" label={{ value: `Meta: ${targets.efficiency}%`, position: 'right', fill: '#10b981', fontSize: 11 }} />
              <Line type="monotone" dataKey="realEfficiency" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3, fill: '#06b6d4' }} />
              <Line type="monotone" dataKey="simEfficiency" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#8b5cf6' }} />
            </LineChart>
          ) : viewMode === 'times' ? (
            <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.axis }} />
              <YAxis tick={{ fontSize: 11, fill: colors.axis }} tickFormatter={(v) => `${v}min`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '8px'
                }}
                formatter={(value, name) => {
                  const labels = { cuttingTime: 'Tempo Corte', machineStopped: 'Máq. Parada', betweenMarkers: 'Entre Marcadas' };
                  return [`${value} min`, labels[name] || name];
                }}
              />
              <Legend formatter={(v) => {
                const labels = { cuttingTime: 'Tempo Corte', machineStopped: 'Máq. Parada', betweenMarkers: 'Entre Marcadas' };
                return labels[v] || v;
              }} />
              <Bar dataKey="cuttingTime" fill="#06b6d4" name="cuttingTime" />
              <Bar dataKey="machineStopped" fill="#ef4444" name="machineStopped" />
              <Bar dataKey="betweenMarkers" fill="#f59e0b" name="betweenMarkers" />
            </ComposedChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.axis }} />
              <YAxis domain={[0, 'auto']} tick={{ fontSize: 11, fill: colors.axis }} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '8px'
                }}
                formatter={(value, name) => {
                  const labels = { stoppedPercent: '% Parada', betweenPercent: '% Entre Marcadas' };
                  return [`${value}%`, labels[name] || name];
                }}
              />
              <Legend formatter={(v) => v === 'stoppedPercent' ? '% Parada (vs T.Corte)' : '% Entre Marcadas (vs T.Corte)'} />
              <ReferenceLine y={targets.machineStopped} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Meta Parada: ${targets.machineStopped}%`, position: 'right', fill: '#ef4444', fontSize: 10 }} />
              <Line type="monotone" dataKey="stoppedPercent" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: '#ef4444' }} />
              <Line type="monotone" dataKey="betweenPercent" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Legend info */}
      <div className={`mt-4 pt-4 border-t ${colors.border} text-xs ${colors.textMut} text-center`}>
        {viewMode === 'efficiency' && 'Comparação entre eficiência real e simulada ao longo do período'}
        {viewMode === 'times' && 'Distribuição de tempos por dia (em minutos)'}
        {viewMode === 'percentages' && 'Percentagem de paragem e entre marcadas relativo ao tempo de corte'}
      </div>
    </div>
  );
});

GoalsEvolutionChart.displayName = 'GoalsEvolutionChart';

export default GoalsEvolutionChart;
